import React, { useEffect, useRef } from 'react';

import QuestionLibraryBuilder from '~/common/components/QuestionLibrary/QuestionLibraryBuilder';
import Heading from '~/common/components/QuestionLibrary/components/Heading';
import AddIconButton from '~/common/components/QuestionLibrary/components/buttons/AddIconButton';
import DeleteIconButton from '~/common/components/QuestionLibrary/components/buttons/DeleteIconButton';
import QuestionResolutionWrapper from '~/common/components/QuestionLibrary/components/utilityComponents/QuestionResolutionWrapper';
import { useQuestionLibraryEventEmitter } from '~/common/components/QuestionLibrary/store/questionLibraryStore';
import { useConditionsEvaluator } from '~/common/components/QuestionLibrary/utils/useConditionsEvaluator';
import useQuestionLibraryFieldArray from '~/common/components/QuestionLibrary/utils/useQuestionLibraryFieldArray';

type FieldArrayLayoutProps = {
  question?: any;
  isParentShowing?: boolean;
};

function FieldArrayLayout({
  question,
  isParentShowing,
}: Readonly<FieldArrayLayoutProps>) {
  const {
    fieldArrayName,
    template,
    title,
    appendItem = {},
    hasBottomBorder = true,
    restrictions = {},
    isAddEnabled = true,
    isDeleteEnabled = true,
    deleteEventEmitterActionName = '',
    addButtonLabel = 'Add',
    resolutions = [],
    conditions = {},
    sx,
    padding = '0 0 0.5rem 0',
    addButtonProps = {},
    minItems = 0,
  } = question;
  const fieldKeys = Object.keys(appendItem);
  const { isVisible } = useConditionsEvaluator(conditions);
  const eventEmitter = useQuestionLibraryEventEmitter();
  const {
    fields,
    layouts,
    handleAppend,
    handleRemove,
    disableAdd,
    disableDelete,
  } = useQuestionLibraryFieldArray({
    name: fieldArrayName,
    restrictions,
    appendItem,
    title,
    template,
    fieldKeys,
  });

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!isVisible) {
      hasInitializedRef.current = false;
      return;
    }
    if (
      !hasInitializedRef.current &&
      minItems > 0 &&
      fields.length < minItems
    ) {
      hasInitializedRef.current = true;
      const itemsToAdd = minItems - fields.length;
      for (let i = 0; i < itemsToAdd; i++) {
        handleAppend(appendItem);
      }
    }
  }, [appendItem, fields.length, handleAppend, isVisible, minItems]);

  const handleAdd = () => {
    handleAppend(appendItem);
  };
  const handleDelete = (index: number) => {
    if (deleteEventEmitterActionName) {
      eventEmitter.emit(deleteEventEmitterActionName, index);
    }
    handleRemove(index);
  };

  return (
    <QuestionResolutionWrapper
      hasBottomBorder={hasBottomBorder}
      resolutions={resolutions}
      isVisible={isVisible}
      padding={padding}
    >
      {layouts.map((layout, index) => {
        return (
          <div key={fields[index].id}>
            {layout.title && (
              <Heading text={layout.title}>
                {isDeleteEnabled && (
                  <DeleteIconButton
                    testId={`delete-${fieldArrayName}-${index + 1}-button`}
                    ariaLabel={`Delete ${layout.title}`}
                    onClick={() => handleDelete(index)}
                    isDisabled={disableDelete}
                  />
                )}
              </Heading>
            )}
            <QuestionLibraryBuilder
              questions={layout.questions}
              isParentShowing={isVisible && isParentShowing}
            />
          </div>
        );
      })}
      {isAddEnabled && (
        <AddIconButton
          testId={`add-${fieldArrayName}-button`}
          handleClick={handleAdd}
          isDisabled={disableAdd}
          label={addButtonLabel}
          sx={sx}
          variant={addButtonProps?.buttonVariant}
          iconVariant={addButtonProps?.iconVariant}
        />
      )}
    </QuestionResolutionWrapper>
  );
}

export default React.memo(FieldArrayLayout);
